/**
 * PropertyContextPanel - Right-side decision support panel
 * Contains: Sticky Map, Price Insights, Saved/Compare Tray
 * Supports hover-to-zoom feature (admin controlled per country)
 * Helps users make faster decisions without distraction
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Map, TrendingUp, TrendingDown, Minus, BarChart3, 
  Home, Scale, ChevronRight, Maximize2, X, Clock, Sparkles
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCountry } from '../context/CountryContext';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icon
const createPriceMarker = (price, isHighlighted = false, formatCurrency) => {
  const displayPrice = price >= 1000 ? `${Math.round(price/1000)}k` : price;
  return L.divIcon({
    className: 'custom-price-marker',
    html: `
      <div class="flex items-center justify-center ${
        isHighlighted 
          ? 'bg-blue-600 text-white shadow-lg scale-110' 
          : 'bg-white text-gray-900 shadow-md'
      } rounded-full px-2 py-1 text-xs font-bold border-2 ${
        isHighlighted ? 'border-blue-400' : 'border-gray-200'
      } transition-all duration-200" style="white-space: nowrap; min-width: 40px;">
        ${displayPrice}
      </div>
    `,
    iconSize: [60, 28],
    iconAnchor: [30, 14],
    popupAnchor: [0, -14]
  });
};

// Map updater component with smooth animation support
function MapUpdater({ center, zoom, animate = false, duration = 500 }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      if (animate) {
        map.flyTo(center, zoom || 15, {
          duration: duration / 1000, // Convert ms to seconds
          easeLinearity: 0.25
        });
      } else {
        map.setView(center, zoom || 13);
      }
    }
  }, [center, zoom, animate, duration, map]);
  return null;
}

// Mini Map Component with hover-to-zoom support
function MiniMap({ 
  listings, 
  center, 
  highlightedId, 
  onMarkerClick, 
  formatCurrency,
  hoverZoomSettings = { enabled: true, zoomLevel: 15, animationDuration: 500, enablePan: true }
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoverTarget, setHoverTarget] = useState(null);
  const mapRef = useRef(null);
  
  const markers = useMemo(() => {
    return listings
      .filter(l => l.latitude && l.longitude)
      .map(listing => ({
        id: listing.id,
        position: [listing.latitude, listing.longitude],
        price: listing.price,
        title: listing.title
      }));
  }, [listings]);

  const mapCenter = center || (markers.length > 0 
    ? [markers[0].position[0], markers[0].position[1]] 
    : [6.9271, 79.8612]);
  
  // Handle hover-to-zoom: when highlightedId changes, zoom to that marker
  useEffect(() => {
    if (!hoverZoomSettings.enabled || !highlightedId) {
      setHoverTarget(null);
      return;
    }
    
    const marker = markers.find(m => m.id === highlightedId);
    if (marker && hoverZoomSettings.enablePan) {
      setHoverTarget({
        center: marker.position,
        zoom: hoverZoomSettings.zoomLevel,
        animate: true,
        duration: hoverZoomSettings.animationDuration
      });
    }
  }, [highlightedId, markers, hoverZoomSettings]);

  if (isExpanded) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl w-full max-w-4xl h-[80vh] overflow-hidden shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="font-semibold flex items-center gap-2">
              <Map className="w-5 h-5" />
              Property Locations
              {hoverZoomSettings.enabled && (
                <Badge variant="secondary" className="ml-2 text-xs">
                  Hover to zoom
                </Badge>
              )}
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setIsExpanded(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>
          <div className="h-[calc(100%-60px)]">
            <MapContainer
              center={mapCenter}
              zoom={13}
              className="h-full w-full"
              scrollWheelZoom={true}
              ref={mapRef}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap'
              />
              {/* Use hoverTarget for animated zoom, fallback to mapCenter */}
              <MapUpdater 
                center={hoverTarget?.center || mapCenter} 
                zoom={hoverTarget?.zoom || 13} 
                animate={hoverTarget?.animate}
                duration={hoverTarget?.duration}
              />
              {markers.map(marker => (
                <Marker
                  key={marker.id}
                  position={marker.position}
                  icon={createPriceMarker(marker.price, marker.id === highlightedId, formatCurrency)}
                  eventHandlers={{
                    click: () => onMarkerClick?.(marker.id)
                  }}
                >
                  <Popup>
                    <div className="text-sm font-medium">{marker.title}</div>
                    <div className="text-blue-600 font-bold">{formatCurrency(marker.price)}/mo</div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className="overflow-hidden" data-testid="context-mini-map">
      <div className="relative h-[280px]">
        <MapContainer
          center={mapCenter}
          zoom={12}
          className="h-full w-full rounded-t-xl"
          scrollWheelZoom={false}
          zoomControl={false}
          ref={mapRef}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OSM'
          />
          {/* Use hoverTarget for animated zoom, fallback to mapCenter */}
          <MapUpdater 
            center={hoverTarget?.center || mapCenter} 
            zoom={hoverTarget?.zoom || 12}
            animate={hoverTarget?.animate}
            duration={hoverTarget?.duration}
          />
          {markers.map(marker => (
            <Marker
              key={marker.id}
              position={marker.position}
              icon={createPriceMarker(marker.price, marker.id === highlightedId, formatCurrency)}
              eventHandlers={{
                click: () => onMarkerClick?.(marker.id)
              }}
            />
          ))}
        </MapContainer>
        
        {/* Expand button */}
        <Button
          size="sm"
          variant="secondary"
          className="absolute top-3 right-3 rounded-full shadow-lg"
          onClick={() => setIsExpanded(true)}
        >
          <Maximize2 className="w-4 h-4" />
        </Button>
        
        {/* Listings count overlay */}
        <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 text-sm font-medium shadow-lg">
          {markers.length} properties on map
        </div>
      </div>
    </Card>
  );
}

// Price Insight Component
function PriceInsight({ listings }) {
  const { formatCurrency, currency } = useCountry();
  
  const stats = useMemo(() => {
    const prices = listings.map(l => l.price).filter(p => p > 0);
    if (prices.length === 0) return null;
    
    const sorted = [...prices].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    const min = sorted[0];
    const max = sorted[sorted.length - 1];
    const avg = Math.round(prices.reduce((a, b) => a + b, 0) / prices.length);
    
    // Calculate percentiles for distribution
    const p25 = sorted[Math.floor(sorted.length * 0.25)];
    const p75 = sorted[Math.floor(sorted.length * 0.75)];
    
    // Count new listings (last 7 days)
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const newCount = listings.filter(l => 
      l.created_at && new Date(l.created_at) > weekAgo
    ).length;
    
    return { median, min, max, avg, p25, p75, newCount, total: prices.length };
  }, [listings]);
  
  if (!stats) return null;
  
  // Simple histogram buckets
  const buckets = useMemo(() => {
    const range = stats.max - stats.min;
    const bucketSize = range / 5;
    const counts = [0, 0, 0, 0, 0];
    
    listings.forEach(l => {
      if (l.price > 0) {
        const idx = Math.min(4, Math.floor((l.price - stats.min) / bucketSize));
        counts[idx]++;
      }
    });
    
    const maxCount = Math.max(...counts);
    return counts.map((count, idx) => ({
      count,
      height: maxCount > 0 ? (count / maxCount) * 100 : 0,
      range: {
        min: Math.round(stats.min + idx * bucketSize),
        max: Math.round(stats.min + (idx + 1) * bucketSize)
      }
    }));
  }, [listings, stats]);
  
  return (
    <Card data-testid="price-insight-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-blue-600" />
          Price Insight
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main stat */}
        <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
          <p className="text-sm text-gray-500 mb-1">Most rentals here</p>
          <p className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {currency}{stats.p25?.toLocaleString()} – {currency}{stats.p75?.toLocaleString()}
          </p>
          <p className="text-xs text-gray-400 mt-1">per month</p>
        </div>
        
        {/* Mini histogram */}
        <div className="px-2">
          <div className="flex items-end justify-between h-12 gap-1">
            {buckets.map((bucket, idx) => (
              <div 
                key={idx}
                className="flex-1 bg-blue-500/20 rounded-t hover:bg-blue-500/40 transition-colors relative group"
                style={{ height: `${Math.max(bucket.height, 8)}%` }}
              >
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block">
                  <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                    {bucket.count} properties
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>{currency}{(stats.min/1000).toFixed(0)}k</span>
            <span>{currency}{(stats.max/1000).toFixed(0)}k</span>
          </div>
        </div>
        
        {/* Quick stats */}
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="p-2 bg-gray-50 rounded-lg">
            <p className="text-gray-500 text-xs">Median</p>
            <p className="font-semibold">{currency}{stats.median?.toLocaleString()}</p>
          </div>
          <div className="p-2 bg-gray-50 rounded-lg">
            <p className="text-gray-500 text-xs">Average</p>
            <p className="font-semibold">{currency}{stats.avg?.toLocaleString()}</p>
          </div>
        </div>
        
        {/* New listings */}
        {stats.newCount > 0 && (
          <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg text-sm">
            <Sparkles className="w-4 h-4 text-emerald-600" />
            <span className="text-emerald-700">
              <strong>{stats.newCount}</strong> new listing{stats.newCount > 1 ? 's' : ''} this week
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Saved & Compare Tray
function SavedCompareTray({ compareIds, onCompare, onClear }) {
  const navigate = useNavigate();
  
  return (
    <Card data-testid="saved-compare-tray">
      <CardContent className="p-4">
        {/* Compare section */}
        {compareIds.length > 0 ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Scale className="w-4 h-4 text-blue-600" />
                <span className="font-medium text-sm">Compare</span>
              </div>
              <Badge variant="secondary">{compareIds.length} selected</Badge>
            </div>
            
            <div className="flex gap-2">
              <Button 
                size="sm" 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={onCompare}
                disabled={compareIds.length < 2}
              >
                Compare Now
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={onClear}
              >
                Clear
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-2">
            <Scale className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Select properties to compare</p>
            <p className="text-xs text-gray-400 mt-1">Click + on listings to add</p>
          </div>
        )}
        
        {/* Quick links */}
        <div className="mt-4 pt-4 border-t space-y-2">
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full justify-start text-gray-600"
            onClick={() => navigate('/my-comparisons')}
          >
            <Home className="w-4 h-4 mr-2" />
            My Saved Comparisons
            <ChevronRight className="w-4 h-4 ml-auto" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full justify-start text-gray-600"
            onClick={() => navigate('/my-alerts')}
          >
            <Clock className="w-4 h-4 mr-2" />
            My Price Alerts
            <ChevronRight className="w-4 h-4 ml-auto" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Main Context Panel Component
export default function PropertyContextPanel({ 
  listings, 
  searchCenter,
  highlightedListingId,
  onMarkerClick,
  compareIds = [],
  onCompare,
  onClearCompare,
  hoverZoomSettings = { enabled: true, zoomLevel: 15, animationDuration: 500, enablePan: true }
}) {
  const { formatCurrency } = useCountry();
  
  return (
    <div className="space-y-4 sticky top-4" data-testid="property-context-panel">
      {/* Mini Map with hover-to-zoom support */}
      <MiniMap 
        listings={listings}
        center={searchCenter}
        highlightedId={highlightedListingId}
        onMarkerClick={onMarkerClick}
        formatCurrency={formatCurrency}
        hoverZoomSettings={hoverZoomSettings}
      />
      
      {/* Price Insight */}
      <PriceInsight listings={listings} />
      
      {/* Saved & Compare Tray */}
      <SavedCompareTray 
        compareIds={compareIds}
        onCompare={onCompare}
        onClear={onClearCompare}
      />
    </div>
  );
}
